<template>
  <div class="flex flex-col items-start self-stretch">
    <div class="flex pb-4 items-center gap-3 self-stretch">
      <h3 class="text-slate-800 dark:text-slate-100 text-base font-medium leading-6">Files</h3>
    </div>
      <div
      v-if="displayFiles && displayFiles.length > 0"
      class="grid grid-cols-4 gap-4 pb-4 items-start self-stretch"
    >
      <div
        v-for="(file, index) in displayFiles"
        :key="index"
        class="relative flex w-[107px] flex-col items-start gap-2"
      >
        <div
          @click="openFileViewer(file)"
          class="cursor-pointer flex h-24 justify-center items-center gap-[10px] self-stretch rounded-lg border border-black/10 overflow-hidden bg-slate-200 dark:bg-slate-700"
          :style="isImageFile(file) ? `background-image: url(${getAdjustedUrl(file)}); background-position: center; background-size: cover; background-repeat: no-repeat;` : ''"
        >
          <template v-if="!isImageFile(file)">
            <div v-if="isPdfFile(file)" class="flex flex-col items-center justify-center w-full h-full">
              <svg class="w-8 h-8 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span class="text-xs text-slate-600 dark:text-slate-300 font-medium mt-1">PDF</span>
            </div>
            <div v-else-if="isWordFile(file)" class="flex flex-col items-center justify-center w-full h-full">
              <svg class="w-8 h-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <span class="text-xs text-slate-600 dark:text-slate-300 font-medium mt-1">DOC</span>
            </div>
            <div v-else class="flex flex-col items-center justify-center w-full h-full">
              <svg class="w-8 h-8 text-slate-500 dark:text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <span class="text-xs text-slate-600 dark:text-slate-300 font-medium mt-1">{{ getFileExtension(file.name || file.file_name) }}</span>
            </div>
          </template>
        </div>

        <span class="text-slate-500 dark:text-slate-400 truncate w-full text-sm font-normal leading-5">{{ file.name || file.file_name }}</span>

        <button
          @click="onDeleteClick(file, index)"
          class="absolute -right-2 -top-2 flex justify-center items-center w-8 h-8 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700"
        >
          <Icon icon="trash" :width="16" :height="16" class="text-slate-500 dark:text-slate-400" />
        </button>
      </div>
    </div>

    <div class="flex items-start">
      <button
        @click="triggerFileUpload"
        class="flex items-start gap-2 rounded px-0 py-0 hover:opacity-80 transition-opacity"
      >
        <Icon icon="paperclip" :width="20" :height="20" class="text-slate-800 dark:text-slate-100" />
        <span class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5">Add files</span>
      </button>

      <input
        type="file"
        @change="handleFileUpload"
        accept="image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        class="hidden"
        ref="fileInput"
        multiple
      />
    </div>

    <teleport to="body">
      <FileViewer
        :show="showFileViewer"
        :file="selectedFile"
        @close="closeFileViewer"
      />
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import FileViewer from './FileViewer.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { useTripsStore } from '@/store/trips';
import { storeToRefs } from 'pinia';
import { notify } from 'notiwind';

const props = defineProps({
  files: {
    type: Array,
    default: () => []
  },
  redemption: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['files-updated']);

const tripsStore = useTripsStore();
const { activeTrip } = storeToRefs(tripsStore);

const selectedFile = ref(null);
const showFileViewer = ref(false);
const fileInput = ref(null);
const uploadingFiles = ref([]);
const localFiles = ref([]);

const displayFiles = computed(() => {
  const propsFiles = props.files || [];
  const allFiles = [...propsFiles, ...localFiles.value, ...uploadingFiles.value];

  const storeAttachments = (tripsStore.activeRedemption?.attachments ?? null);
  const hasAuthoritative = Array.isArray(storeAttachments);
  const idSet = new Set(
    (storeAttachments || [])
      .map(it => (typeof it === 'object' ? it.id : it))
      .filter(Boolean)
  );

  const uniqueFiles = [];
  const seen = new Set();
  for (const f of allFiles) {
    const key = f?.id || `${f?.url || f?.cdnUrl || f?.file_name || f?.name}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniqueFiles.push(f);
    }
  }

  if (!hasAuthoritative) {
    return uniqueFiles;
  }

  return uniqueFiles.filter(f => {
    if (f?.isUploading) return true;
    if (!f?.id) return false;
    return idSet.has(f.id);
  });
});

function triggerFileUpload() {
  fileInput.value.click();
}

async function handleFileUpload(event) {
  const files = Array.from(event.target.files);
  if (!files || files.length === 0) return;

  if (!props.redemption || !props.redemption.id) {
    notify({
      group: 'warning',
      title: 'Cannot upload files',
      text: 'Please save the redemption first before adding files',
    });
    event.target.value = '';
    return;
  }

  await validateAndProcessFiles(files);
  event.target.value = '';
}

const uploadToCDN = async (file) => {
  const fileName = file.name;
  try {
    const response = await window.ApiService.uploadFile(
      `/trips/${activeTrip.value.uuid}/redemptions/upload`,
      file,
      {},
      {}
    );

    const { data } = response;

    if (!data.files || !data.files.length) {
      throw new Error('Server response missing files');
    }

    const uploadedFile = data.files[0];
    const finalUrl = uploadedFile.url;

    return {
      id: uploadedFile.id,
      url: finalUrl,
      name: uploadedFile.name,
      file_name: uploadedFile.file_name || uploadedFile.name,
      extension: uploadedFile.extension
    };
  } catch (err) {
    notify({
      group: 'error',
      title: 'Upload Error',
      text: err.message || `Could not upload ${fileName}`,
    });
    return null;
  }
};

const validateAndProcessFiles = async (files) => {
  const validTypes = [
    'image/jpeg',
    'image/png',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];
  const maxSizeMB = 10;

  const validFiles = Array.from(files).filter((file) => {
    const isValidType = validTypes.includes(file.type);
    const isValidSize = file.size <= maxSizeMB * 1024 * 1024;

    if (!isValidType) {
      notify({
        group: 'error',
        title: 'Invalid file type',
        text: `${file.name} is not a supported file type`,
      });
      return false;
    }

    if (!isValidSize) {
      notify({
        group: 'error',
        title: 'File too large',
        text: `${file.name} exceeds ${maxSizeMB}MB`,
      });
      return false;
    }

    return true;
  });

  if (validFiles.length === 0) return;

  try {
    const filesToUpload = validFiles.map((file) => {
      const preview = {
        name: file.name,
        type: file.type,
        size: file.size,
        isUploading: true
      };

      if (file.type.includes('image')) {
        preview.previewUrl = URL.createObjectURL(file);
      }

      return { file, preview };
    });

    const previews = filesToUpload.map(f => f.preview);
    uploadingFiles.value = [...uploadingFiles.value, ...previews];

    let successCount = 0;
    const uploadedFileObjects = [];

    for (let i = 0; i < filesToUpload.length; i++) {
      const { file, preview } = filesToUpload[i];
      const uploadResult = await uploadToCDN(file);

      const fileIndex = uploadingFiles.value.findIndex(
        f => f.name === preview.name && f.isUploading === true
      );

      if (uploadResult && fileIndex !== -1) {
        if (file.type.includes('image') && preview.previewUrl) {
          URL.revokeObjectURL(preview.previewUrl);
        }

        const imageUrl = uploadResult.url;

        uploadingFiles.value[fileIndex] = {
          id: uploadResult.id,
          name: uploadResult.name || file.name,
          size: file.size,
          type: file.type,
          previewUrl: file.type.includes('image') ? imageUrl : null,
          cdnUrl: imageUrl,
          url: imageUrl,
          file_name: uploadResult.file_name || uploadResult.name || file.name,
          extension: uploadResult.extension,
          isUploading: false
        };

        uploadedFileObjects.push({
          id: uploadResult.id,
          url: uploadResult.url,
          name: uploadResult.name,
          file_name: uploadResult.file_name || uploadResult.name,
          type: file.type
        });

        successCount++;
      } else if (fileIndex !== -1) {
        uploadingFiles.value.splice(fileIndex, 1);
      }
    }

    if (successCount > 0) {
      uploadingFiles.value = [];

      localFiles.value = [...localFiles.value, ...uploadedFileObjects];

      await updateRedemptionWithFiles(uploadedFileObjects);

      notify({
        group: 'success',
        title: 'Files uploaded',
        text: `Successfully uploaded ${successCount} file(s)`,
      });
    }
  } catch (error) {
    notify({
      group: 'error',
      title: 'Upload failed',
      text: error.message || 'There was an error uploading your files. Please try again.',
    });
  }
}

async function updateRedemptionWithFiles(uploadedFiles) {
  try {
    const existingAttachments = props.redemption.attachments || [];

    const combined = [...existingAttachments, ...uploadedFiles];
    const attachmentIds = combined
      .filter(f => f && f.id)
      .map(f => ({ id: f.id }));

    const currentRedemption = { ...tripsStore.activeRedemption };
    currentRedemption.attachments = attachmentIds;
    currentRedemption.files = attachmentIds;

    tripsStore.setActiveRedemption(currentRedemption);

    const response = await tripsStore.updateRedemption({ attachmentsOnly: true, silent: true });

    if (response && response.status === 200) {
      const serverAttachments = response.data?.redemption?.attachments;
      if (serverAttachments && Array.isArray(serverAttachments)) {
        emit('files-updated', serverAttachments);
        localFiles.value = serverAttachments;
      } else {
        const fullObjects = combined.filter(f => f && f.id);
        emit('files-updated', fullObjects);
        localFiles.value = fullObjects;
      }
    }

  } catch (error) {
    notify({
      group: 'error',
      title: 'Failed to save files',
      text: 'There was an error saving the files. Please try again.',
    });
    throw error;
  }
}

function removeUploadingFile(index) {
  uploadingFiles.value.splice(index, 1);
}

async function onDeleteClick(file, index) {
  if (file.isUploading) {
    removeUploadingFile(index);
    return;
  }

  try {
    const currentDisplay = (props.files || []).slice();
    const targetId = file.id || file.uuid;
    const updatedDisplay = currentDisplay.filter((f) => (f.id || f.uuid) !== targetId);

    const base = { ...tripsStore.activeRedemption };
    const attachmentIds = updatedDisplay
      .filter(it => it.id)
      .map(it => ({ id: it.id }));
    base.attachments = attachmentIds;
    base.files = attachmentIds;
    tripsStore.setActiveRedemption(base);

    const response = await tripsStore.updateRedemption({ attachmentsOnly: true, silent: true });
    if (response && response.status === 200) {
      localFiles.value = updatedDisplay;
      emit('files-updated', updatedDisplay);

      notify({
        group: 'success',
        title: 'File deleted',
        text: 'The file was removed successfully.'
      });
    }
  } catch (error) {
    notify({
      group: 'error',
      title: 'Failed to delete file',
      text: error?.message || 'There was an error removing the file. Please try again.'
    });
  }
}

watch(() => props.files, (newFiles) => {
  if (newFiles && newFiles.length > 0 && localFiles.value.length > 0) {
    const localFileIds = localFiles.value.map(f => f.id);
    const newFileIds = newFiles.map(f => f.id);
    const localFilesIncluded = localFileIds.every(id => newFileIds.includes(id));

    if (localFilesIncluded) {
      localFiles.value = [];
    }
  }
}, { deep: true });

function getAdjustedUrl(file) {
  if (!file) return '';

  if (file._resolvedUrl) return file._resolvedUrl;

  if (file.previewUrl) {
    file._resolvedUrl = file.previewUrl;
    return file._resolvedUrl;
  }

  let baseUrl = file.cdnUrl || file.url || '';

  const isImage = isImageFile(file);
  const hasQuery = typeof baseUrl === 'string' && baseUrl.includes('?');
  if (isImage && baseUrl && !hasQuery) {
    const candidateFile = file.file_name || file.name;
    if (candidateFile) {
      const endsWithFile = baseUrl.endsWith(candidateFile);
      if (!endsWithFile) {
        if (!baseUrl.endsWith('/')) {
          baseUrl += '/';
        }
        baseUrl += candidateFile;
      }
    }
  }

  file._resolvedUrl = baseUrl;
  return file._resolvedUrl;
}

function openFileViewer(file) {
  selectedFile.value = file;
  showFileViewer.value = true;
}

function closeFileViewer() {
  showFileViewer.value = false;
  selectedFile.value = null;
}

const isImageFile = (file) => {
  const type = file?.type || file?.mime_type || '';
  if (type && (type.includes('image') || type.includes('jpeg') || type.includes('jpg') || type.includes('png') || type.includes('gif'))) {
    return true;
  }

  const fileName = file?.file_name || file?.name || '';
  const lowerName = fileName.toLowerCase();
  return lowerName.endsWith('.jpg') ||
         lowerName.endsWith('.jpeg') ||
         lowerName.endsWith('.png') ||
         lowerName.endsWith('.gif') ||
         lowerName.endsWith('.webp') ||
         lowerName.endsWith('.svg');
};

function getFileExtension(fileName) {
  if (!fileName) return 'FILE';
  const parts = fileName.split('.');
  return parts.length > 1 ? parts.pop().toUpperCase() : 'FILE';
}

function isPdfFile(file) {
  if (!file) return false;
  const type = file?.type || file?.mime_type || '';
  const name = file?.name || file?.file_name || '';

  return type.includes('pdf') || name.toLowerCase().endsWith('.pdf');
}

function isWordFile(file) {
  if (!file) return false;
  const type = file?.type || file?.mime_type || '';
  const name = file?.name || file?.file_name || '';
  const lowerName = name.toLowerCase();

  return type.includes('word') ||
         type.includes('document') ||
         lowerName.endsWith('.doc') ||
         lowerName.endsWith('.docx') ||
         lowerName.endsWith('.rtf');
}

</script>
